import { useEffect, useRef, useState } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

import { ApiError } from '../../api/index.ts';
import type { RoleSummary, UserSummary } from '../../api/types.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { QuietButton } from '../../components/QuietButton.tsx';
import { actionError } from '../../components/actionError.ts';
import { useFullScreenDialog } from '../../hooks/useFullScreenDialog.ts';
import {
  buildCreateUserPayload,
  buildUpdateUserPatch,
  initialUserForm,
  validateUserForm,
  type UserFormState,
} from './usersHelpers.ts';

export interface UserFormDialogProps {
  open: boolean;
  /** The user being edited, or null to create a new one. */
  user: UserSummary | null;
  onClose: () => void;
  onSaved: () => void;
}

/** Role menu items: the fetched catalog once it resolves, or (while loading
 *  or on a roles:manage-forbidden session) a single disabled entry for the
 *  user's current role so the Select never renders an out-of-range value. */
function roleMenuItems(roles: RoleSummary[] | null, current: UserSummary['role']): React.JSX.Element[] {
  if (roles) {
    return roles.map((r) => (
      <MenuItem key={r.id} value={r.id}>
        {r.name}
      </MenuItem>
    ));
  }
  if (current) {
    return [
      <MenuItem key={current.id} value={current.id} disabled>
        {current.name}
      </MenuItem>,
    ];
  }
  return [];
}

export function UserFormDialog({ open, user, onClose, onSaved }: Readonly<UserFormDialogProps>): React.JSX.Element {
  const { api, state } = useAuth();
  const isEdit = user !== null;
  const isSelf = user !== null && user.id === state.userId;
  const fullScreen = useFullScreenDialog();

  const [form, setForm] = useState<UserFormState>(() => initialUserForm(user));
  const [roles, setRoles] = useState<RoleSummary[] | null>(null);
  const [rolesForbidden, setRolesForbidden] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [busy, setBusy] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setForm(initialUserForm(user));
    setError('');
    setEmailError('');
    setBusy(false);
    setRoles(null);
    setRolesForbidden(false);
    submittingRef.current = false;

    let cancelled = false;
    api.roles
      .list()
      .then((res) => {
        if (!cancelled) setRoles(res.roles);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 403) setRolesForbidden(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user, api]);

  const set = <K extends keyof UserFormState>(key: K, value: UserFormState[K]) => {
    setError('');
    setEmailError('');
    setForm((f) => ({ ...f, [key]: value }));
  };

  const roleLocked = busy || isSelf || roles === null;

  const submit = async () => {
    if (submittingRef.current) return;
    // The picker only truly matters when creating and it resolved; an edit
    // that isn't touching the role rides the existing role_id through unread.
    const roleRequired = !isEdit && !rolesForbidden;
    const problem = validateUserForm(form, isEdit, roleRequired);
    if (problem) {
      setError(problem);
      return;
    }
    submittingRef.current = true;
    setBusy(true);
    setError('');
    setEmailError('');
    try {
      if (isEdit && user) {
        const patch = buildUpdateUserPatch(form, user, isSelf);
        if (Object.keys(patch).length === 0) {
          onClose();
          return;
        }
        await api.users.update(user.id, patch);
      } else {
        await api.users.create(buildCreateUserPayload(form));
      }
      onSaved();
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'email_taken') {
        setEmailError(e.message);
      } else {
        setError(actionError(e));
      }
    } finally {
      setBusy(false);
      submittingRef.current = false;
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs" fullScreen={fullScreen}>
      <DialogTitle>{isEdit ? 'Edit user' : 'Add user'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            fullWidth
            disabled={busy}
            error={emailError !== ''}
            helperText={emailError || undefined}
            slotProps={{ htmlInput: { 'aria-label': 'user-email' } }}
          />
          <TextField
            label="Display name"
            value={form.display_name}
            onChange={(e) => set('display_name', e.target.value)}
            fullWidth
            disabled={busy}
            helperText={isEdit ? 'Leave blank to keep the current name' : undefined}
            slotProps={{ htmlInput: { 'aria-label': 'user-display-name' } }}
          />
          <Tooltip title={isSelf ? 'You cannot change your own role.' : ''} disableHoverListener={!isSelf}>
            <FormControl fullWidth disabled={roleLocked}>
              <InputLabel id="user-role-label">Role</InputLabel>
              <Select<number | ''>
                labelId="user-role-label"
                label="Role"
                value={form.role_id}
                onChange={(e) => set('role_id', e.target.value === '' ? '' : Number(e.target.value))}
                inputProps={{ 'aria-label': 'user-role' }}
              >
                {roleMenuItems(roles, user?.role ?? null)}
              </Select>
              {isSelf && <FormHelperText>You cannot change your own role.</FormHelperText>}
              {!isSelf && rolesForbidden && (
                <FormHelperText>Role list requires the Manage roles permission.</FormHelperText>
              )}
            </FormControl>
          </Tooltip>
          <TextField
            label={isEdit ? 'New password (leave blank to keep)' : 'Password'}
            type="password"
            value={form.password}
            onChange={(e) => set('password', e.target.value)}
            fullWidth
            disabled={busy}
            autoComplete="new-password"
            slotProps={{ htmlInput: { 'aria-label': 'user-password' } }}
          />
          {isEdit && (
            <Tooltip title={isSelf ? 'You cannot deactivate your own account.' : ''} disableHoverListener={!isSelf}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_active}
                    disabled={busy || isSelf}
                    onChange={(e) => set('is_active', e.target.checked)}
                    slotProps={{ input: { 'aria-label': 'user-active' } }}
                  />
                }
                label="Active"
              />
            </Tooltip>
          )}
          {error && (
            <Alert role="alert" severity="error" sx={{ fontWeight: 700 }}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <QuietButton onClick={onClose} disabled={busy}>
          Cancel
        </QuietButton>
        <Button onClick={() => void submit()} disabled={busy}>
          {isEdit ? 'Save changes' : 'Create user'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default UserFormDialog;
